import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, ViewChild, SimpleChange } from '@angular/core';
import { CdkDrag, CdkDragDrop, CDK_DRAG_CONFIG, moveItemInArray } from '@angular/cdk/drag-drop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Util } from '@libs/utilities/util';
import { KeyboardMouseService } from '@libs/libraries/frontend';

import { NgTagInputModel } from './tag-input.model';
import { NG_TAG_INPUT_MODES } from './tag-input.data';
import { NgAbstractInputComponent } from '../shared/inputs.abstract';
import { NgMessageToastAssistant } from '../../dialogs';
import { EnumNgFaStyle } from '../../../styling-components';
import { INgTagInputEvent, INgTagInputConfig, EnumNgTagInputAction, EnumNgSuggestionType, EnumNgSuggestionMode, EnumNgAlignment, EnumNgCasingType, TypeNgTagInputAppearance, TypeNgValidationAndEscapeFn } from './tag-input.interface';


@Component({
    selector: 'ng-tag-input',
    templateUrl: './tag-input.component.html',
    styleUrls: ['./tag-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgTagInputComponent,
            multi: true // for the NgModal (used to bind the keywords)
        },
        {
            provide: CDK_DRAG_CONFIG,
            useValue: { zIndex: 9999 } // Drag & Drop Config
        },
        NgTagInputModel,
        KeyboardMouseService
    ],
    standalone: false
})
export class NgTagInputComponent extends NgAbstractInputComponent<string[]> implements OnInit, ControlValueAccessor, OnChanges, AfterViewInit {

	@ViewChild('searchInput') 	 searchInput	!: ElementRef<HTMLInputElement>;
	@ViewChild('inputBlock') 	 inputBlock		!: ElementRef<HTMLElement>;
	@ViewChild('dropdownBlock')  dropdownBlock	!: ElementRef<HTMLElement>;
	@ViewChild('valueContainer') valueContainer	!: ElementRef<HTMLElement>;

	@Input() suggestions		: string[] = [];
	@Input() config			   !: Partial<INgTagInputConfig>;
	@Input() appearance		: TypeNgTagInputAppearance = 'accent';
	@Input() validateAndEscape !: TypeNgValidationAndEscapeFn;			// validateAndEscape(tags: string): boolean;

	@Output() readonly actionEvent: EventEmitter<INgTagInputEvent> = new EventEmitter();

	//** Getter Helpers */
	readonly EnumNgSuggestionType	: typeof EnumNgSuggestionType = EnumNgSuggestionType;
	readonly EnumNgAlignment		: typeof EnumNgAlignment 	  = EnumNgAlignment;
	readonly EnumNgFaStyle			: typeof EnumNgFaStyle 		  = EnumNgFaStyle;

	//** Helper Variables */
	hoveredElementIndex: number | null = null;

	constructor(
		private elem				 : ElementRef,
		private keyboardMouseService : KeyboardMouseService,
		private messageToastAssistant: NgMessageToastAssistant,
		public  uiModel				 : NgTagInputModel
	) {
		super('tag-input');
	}


	/**------------------------------------------------------
	 * Lifecycle Hooks
	 */
	ngOnInit(): void {

		// update mode
		this.uiModel.data.mode = NG_TAG_INPUT_MODES[this.uiModel.data.config.editMode];
	}

	ngOnChanges(changes: { config: SimpleChange }): void {

		//0 - update config
		if (changes.config) this.uiModel.updateConfig(this.config);

		//1 - update keyword position
		this.updatePosition();
	}

	ngAfterViewInit(): void {

		// add paste event listener
		if (this.searchInput) {
			this.searchInput.nativeElement.addEventListener('paste', (event: ClipboardEvent) => {
				this.onPaste(event);
			});
		}
	}


	/**------------------------------------------------------
	 * Control Value Accessor
	 * > Are required because of the interface
	 */
	override writeValue(keywords: string[]): void {
		if (Util.Basic.isDefined(this.searchInput?.nativeElement)) this.searchInput.nativeElement.value = '';
		this.uiModel.data.tagControl.selectedItems = keywords || [];
	}


	/**------------------------------------------------------
	 * Add Tag
	 */
	addSuggestion(tagString: string): void {

		const suggestionsList  : string[] = this.getSuggestionList();
		const suggestionAddable: boolean  = suggestionsList.includes(tagString);
		if (suggestionAddable) this.addTags(tagString);
			else this.addTags(this.searchInput.nativeElement.value);
	}

	addTags(tagString: string): void {

		//0 - split the tags and filter the empty
		const tags: string[] = tagString.split(',')
			.map((tag: string) => tag.trim())
			.filter((tag: string) => tag.length > 0);

		//1 - add each tag and validate
		for (const tag of tags) this.addTag(tag);
	}

	addTag(tag: string): void {

		//0 - reset search input
		if (Util.Basic.isDefined(this.searchInput)) this.searchInput.nativeElement.value = '';

		//1 - check if allowed custom tag
		if (!this.uiModel.data.mode.customTag && !this.checkTagFromSuggestions(this.suggestions, tag)) return;

		//2 - check tag length limitation
		if (tag.length > this.uiModel.data.config.maxTagLength!) this.maximumLimitToast(tag.trim().length);
		tag = tag.substring(0, this.uiModel.data.config.maxTagLength).trim();

		//3 - ignore the casing
		if (Util.String.isEmpty(tag)) return;
		if (this.uiModel.data.config.casingType === EnumNgCasingType.LowerCase) tag = tag.toLowerCase();
		if (this.uiModel.data.config.casingType === EnumNgCasingType.UpperCase) tag = tag.toUpperCase();

		//4 - check & validate by the parent component
		if (Util.Basic.isDefined(this.validateAndEscape)) {
			tag = this.validateAndEscape(tag);
			if (Util.String.isEmpty(tag)) return;
		}

		//5 - shake the matched value element
		for (const index of this.uiModel.data.tagControl.selectedItems.keys()) {
			if (this.uiModel.data.tagControl.selectedItems[index] === tag) this.shakeElement(index);
		}

		//6 - check for duplications
		if (!this.uiModel.data.config.duplication && Util.Array.contains(this.uiModel.data.tagControl.selectedItems, tag)) return;

		//7 - check max tags
		if (this.uiModel.data.tagControl.selectedItems.length >= this.uiModel.data.config.maxTags) {
			this.updateActions(EnumNgTagInputAction.MaxLimit, tag);
			return;
		}

		//8 - update the selected list and emit the action
		this.uiModel.data.suggestions.isActive = false;
		this.uiModel.data.tagControl.selectedItems.push(tag);
		this.updateActions(EnumNgTagInputAction.Add, tag);
	}

	onPaste(event: ClipboardEvent): void {
		const clipboardData	: DataTransfer = event.clipboardData!;
		const pastedText	: string	   = clipboardData.getData('text');
		this.addTags(pastedText);
		setTimeout(() => { 									// WORKAROUND: required because the ui needs some time to load
			if (Util.Basic.isDefined(this.searchInput)) this.searchInput.nativeElement.value = '';
		}, 5);
	}


	/**------------------------------------------------------
	 * Actions
	 */
	openSuggestionDropdown(): void {

		//0 - reset active tab and if added tags reached to the maximum limit then dropdown can't be open
		this.uiModel.data.tagControl.activeTag = null!;
		if (this.uiModel.data.config.maxTags <= this.uiModel.data.tagControl.selectedItems.length) return;

		//1 - open drop down, set the position & focus
		this.uiModel.data.suggestions.isActive = true;
		this.updatePosition();
		this.inputBlock.nativeElement.focus();
	}

	//** Remove tag from list */
	removeTag(index: number): void {
		const tag: string[] = this.uiModel.data.tagControl.selectedItems.splice(index, 1);
		this.updateActions(EnumNgTagInputAction.Remove, tag[0]);
	}


	/**------------------------------------------------------
	 * Drag & Drop
	 */

	//** Update the position of item */
	moveItem(event: CdkDragDrop<any>): void {
		moveItemInArray(this.uiModel.data.tagControl.selectedItems, event.previousContainer.data.index, event.container.data.index);
	}

	//** Update drag placeholder height and width */
	onDragStart(event: { source: CdkDrag }): void {

		//0 - reset, blur and remove selection from tag
		this.uiModel.data.tagControl.activeTag = null!;
		this.inputBlock.nativeElement.blur();
		this.removeAllRanges(); 						// remove all selection

		//1 - set drag placeholder
		this.uiModel.data.dragPlaceholder = {
			height	: event.source.element.nativeElement.offsetHeight,
			width	: event.source.element.nativeElement.offsetWidth
		};
	}

	//** Reset drag placeholder height and width */
	onDragEnd(): void {
		this.uiModel.data.dragPlaceholder = {
			height	: 0,
			width	: 0
		};
	}


	/**------------------------------------------------------
	 * Key Events
	 */

	//** Key stroke event */
	keyStrokeEvent(): void {
		this.updateActions(EnumNgTagInputAction.Keystroke, this.searchInput.nativeElement.value);
	}

	//** On backspace */
	onBackspace(event: KeyboardEvent): void {

		//0 - check if any tag element should be removed
		if (!this.keyboardMouseService.isKeyBackspace(event)) return;
		if (!this.uiModel.data.mode.remove || this.uiModel.data.tagControl.selectedItems.length <= this.uiModel.data.config.minTags) return;

		//1 - check if user wants to remove the types text (not a tag)
		if (Util.Basic.isDefined(this.searchInput) && Util.String.isNotEmpty(this.searchInput.nativeElement.value)) return;

		//2 - remove the last tag item
		const tag: string[] = this.uiModel.data.tagControl.selectedItems.splice(-1);
		this.updateActions(EnumNgTagInputAction.Remove, tag[0]);
	}

	//** Arrow up & down event */
	arrowEvent(focusedElemIndex: number): void {

		//0 - get filtered item length and set index
		const filteredTagsLength: number = this.suggestions.length;
		if (filteredTagsLength <= focusedElemIndex || focusedElemIndex < 0) return;
		this.uiModel.data.tagControl.focusedElemIndex = focusedElemIndex;

		//1 - get all element with suggestion-option class
		const data: any = this.elem.nativeElement.querySelectorAll('.suggestion-option');
		if (!data.length) return;
		const element: HTMLInputElement = data[this.uiModel.data.tagControl.focusedElemIndex] as HTMLInputElement;

		//2 - scroll design into the view
		this.dropdownBlock.nativeElement.scrollTo({
			top		 : element.offsetTop - element.offsetHeight - 10,
			behavior : 'smooth'
		});
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	disableContextMenu(event: MouseEvent): void {
		event.preventDefault();
	}

	onTagClick(event: MouseEvent): void {

		//0 - check if it is a click with control key
		if (!this.keyboardMouseService.isAnyControlKeyPressed(event)) return;

		//1 - emit mouse action event
		const data: INgTagInputEvent = {
			action : EnumNgTagInputAction.MouseClick,
			value  : this.uiModel.data.tagControl.selectedItems[this.uiModel.data.tagControl.selectedItems.length - 1],
			tags   : this.uiModel.data.tagControl.selectedItems,
			event  : event
		};
		this.actionEvent.emit(data);
	}

	//** Update in ngModel */
	onEnterUpdateTag(): void {

		//0 - check if search input not loaded
		if (Util.Basic.isUndefined(this.searchInput)) return;

		//1 - update tags
		if (this.isOpenSuggestion()) this.addSuggestion(this.getSuggestionList()[this.uiModel.data?.tagControl?.focusedElemIndex]);
		else this.addTags(this.searchInput.nativeElement.value);
	}

	//** Update the actions */
	private updateActions(type: EnumNgTagInputAction, value: string) {
		this.updatePosition();
		const data: INgTagInputEvent = {
			action : type,
			value  : value,
			tags   : this.uiModel.data.tagControl.selectedItems
		};
		this.actionEvent.emit(data);
	}

	//** Updating the positioning of the dropdown */
	private updatePosition() {
		setTimeout(() => {
			if (Util.Basic.isUndefined(this.searchInput)) return;

			//0 - update position for inline suggestion type
			if (Util.Basic.isDefined(this.uiModel.data.config?.suggestion) && this.uiModel.data.config.suggestion!.type === EnumNgSuggestionType.Inline) {
				this.updateInlinePosition();
			}

			//1 - update position for filled suggestion type
			if (Util.Basic.isDefined(this.uiModel.data.config?.suggestion) && this.uiModel.data.config.suggestion!.type === EnumNgSuggestionType.Filled) {
				this.updateFilledPosition();
			}

			//2 - reset the focus of any focused tag
			this.uiModel.data.tagControl.focusedElemIndex = 0;
		}, 10);
	}

	updateFilledPosition(): void {

		//0 - get axes of input block
		if (Util.Basic.isUndefined(this.inputBlock)) return;
		if (Util.Basic.isUndefined(this.dropdownBlock)) return;
		const { x, y } = this.inputBlock.nativeElement.getBoundingClientRect();

		//1 - set y axes
		if (window.innerHeight < y + this.dropdownBlock.nativeElement.offsetHeight) this.dropdownBlock.nativeElement.style.bottom = '35px';

		//2 - set x axes
		if (window.innerWidth < x + this.dropdownBlock.nativeElement.offsetWidth) this.dropdownBlock.nativeElement.style.right = '0px';
	}

	updateInlinePosition(): void {

		//0 - get axes of input block
		if (Util.Basic.isUndefined(this.searchInput)) return;
		const { x, y } = this.searchInput.nativeElement.getBoundingClientRect();
		if (Util.Basic.isUndefined(this.dropdownBlock)) return;

		//1 - set y axes
		if (window.innerHeight < y + this.dropdownBlock.nativeElement.offsetHeight) this.dropdownBlock.nativeElement.style.bottom = '65px';

		//2 - set x axes
		if (window.innerWidth < x + this.dropdownBlock.nativeElement.offsetWidth) this.dropdownBlock.nativeElement.style.right = '0px';
	}

	//** Allow to open suggestion list */
	isOpenSuggestion(): boolean {
		if (this.uiModel.data.suggestions.isActive && this.suggestions.length) return true;
		return false;
	}

	//** On outside click reset the things */
	outsideClick(isOutsideClick: boolean): void {
		if (!isOutsideClick) return;

		//0 - close dropdown
		this.uiModel.data.suggestions.isActive = false;
		this.uiModel.data.tagControl.activeTag = null!;

		//1 - if user clicks outside, add the remaining text in the input as tag
		if (Util.Basic.isDefined(this.searchInput) && Util.Basic.isDefined(this.searchInput.nativeElement.value)) {
			this.addTags(this.searchInput.nativeElement.value);
		}
	}

	//** Update the blur status of editable filed */
	setAsBlur(index: number): void {

		//0 - target html element set as blur
		const data  : NodeList    = this.valueContainer.nativeElement.querySelectorAll('.value-label');
		const target: HTMLElement = data[index] as HTMLSpanElement;
		target.blur();

		//1 - reset active control
		this.uiModel.data.tagControl.activeTag = null!;

		//2 - get text content
		if (target.textContent!.length > this.uiModel.data.config.maxTagLength!) this.maximumLimitToast(target.textContent!.length);
		const textContent: string = target.textContent!.substring(0, this.uiModel.data.config.maxTagLength).trim();

		//3 - shake the matched element
		let matched: boolean = false;
		for (const matchedIndex of this.uiModel.data.tagControl.selectedItems.keys()) {
			if (this.uiModel.data.tagControl.selectedItems[matchedIndex] === textContent && matchedIndex !== index) {
				matched = true;
				this.shakeElement(matchedIndex);
			}
		}

		//4 - update and remove tag
		if (matched) {
			this.removeTag(index);
		} else {
			this.uiModel.data.tagControl.selectedItems[index] = textContent;
			target.textContent = textContent;
		}

		//5 - if content is active then remove tag and update the action
		if (Util.String.isEmpty(target.textContent)) this.removeTag(index);
		this.updateActions(EnumNgTagInputAction.Edit, textContent);
	}

	//** Show max limit toastr */
	maximumLimitToast(keywordLength: number): void {
		this.messageToastAssistant.closeAll();
		this.messageToastAssistant.showWarning('Maximum Limit Exceeded', `The limit for keywords is ${this.uiModel.data.config.maxTagLength} (entered length: ${keywordLength})`);
	}

	//** Check tag already added */
	private checkTagFromSuggestions(list: string[], text: string) {

		//1 - check list available
		if (Util.Array.isNotArray(list)) return false;

		//2 - get tags
		const tag: string = list.find((item: string) => item.toLowerCase() === text.toLowerCase())!;
		return Util.String.isNotEmpty(tag) ? true : false;
	}

	//** Mark focused to tag */
	markAsFocus(event: Event): void {
		if (!this.valueContainer.nativeElement.contains(event.target as HTMLElement) && this.searchInput) {
			this.searchInput.nativeElement.focus();
		}
	}

	//** Shake the tag */
	private shakeElement(index: number) {

		//0 - get node-list with contain value class
		const nodeList: NodeListOf<Element> = this.inputBlock.nativeElement.querySelectorAll('.value');

		//1 - check valid node index
		if (index < 0 || index >= nodeList.length) return;

		//2 - set element and add/remove shake elem class
		const element: Element = nodeList[index];
		element.classList.add('shake-elem');
		setTimeout(() => {
			element.classList.remove('shake-elem');
		}, 500);
	}

	//** Remove selected range */
	private removeAllRanges() {
		const selection: Selection = window.getSelection()!;
		selection.removeAllRanges();
	}


	/**------------------------------------------------------
	 * Event Handler Helpers
	 */
	onTagEdit(index: number): void {

		//0 - on tag edit
		this.uiModel.data.tagControl.activeTag = index;
		const elem: Element = this.inputBlock.nativeElement.querySelectorAll('.value-label')[index];

		//1 - add selection range
		const selection	: Selection = window.getSelection()!;
		const range		: Range 	= document.createRange();
		range.selectNodeContents(elem);
		selection.removeAllRanges();
		selection.addRange(range);
	}

	//** Detect on tag focused */
	onTagFocused(index: number): void {
		if (index === this.uiModel.data.tagControl.activeTag) {
			this.onTagEdit(index);
			return;
		}
		this.uiModel.data.tagControl.activeTag = index;
		this.removeAllRanges();
	}

	//** HostListener to close suggestion on escape click */
	@HostListener('document:keydown', ['$event'])
	onKeyEscapeHandler(event: KeyboardEvent): void {
		if (this.keyboardMouseService.isKeyEscape(event)) {
			this.uiModel.data.suggestions.isActive = false;
		}
	}

	//** Get suggestions list */
	getSuggestionList(): string[] {

		//0 - check if config and search input not available
		if (Util.Basic.isUndefined(this.uiModel.data.config) || Util.Basic.isUndefined(this.searchInput)) return [];

		//1 - check suggestion or or suggestion mode is not available
		if (Util.Basic.isUndefined(this.uiModel.data.config.suggestion) || Util.Basic.isUndefined(this.uiModel.data.config.suggestion!.mode)) return this.suggestions;

		//2 - check if mode is manual then return all suggestions
		if (this.uiModel.data.config.suggestion!.mode === EnumNgSuggestionMode.Manual) return this.suggestions;

		//3 - get suggestion which is not added
		const suggestions: string[] = this.suggestions.filter((suggestion: string) => {
			const selectedItems: string[] = Util.String.toLowerCaseArray(this.uiModel.data.tagControl.selectedItems);
			if (!selectedItems.includes(suggestion.toLowerCase())) return suggestion;
			return [];
		});

		//4 - get the user input data, if not exists, return all suggestions
		const searchValue: string = this.searchInput.nativeElement.value;
		if (!Util.Basic.isDefined(searchValue)) return suggestions;

		//5 - get suggestions filter on the basis of input data
		return suggestions.filter((suggestion: string) => suggestion.toLowerCase().includes(searchValue.toLowerCase()));
	}
}
